export type MaesterFrequency = 'manual' | 'weekly' | 'monthly' | 'quarterly';

export interface MaesterLatestScan {
  id: number;
  status: string;
  startedAt?: string | null;
  completedAt?: string | null;
  totalCount: number;
  passedCount: number;
  failedCount: number;
  otherCount: number;
  htmlReportUrl?: string | null;
  excelReportUrl?: string | null;
  jsonReportUrl?: string | null;
  errorMessage?: string | null;
}

export interface MaesterValidationCheck {
  key: string;
  label: string;
  ok: boolean;
  status: number;
  message: string;
}

export interface MaesterValidationDetails {
  checkedAt?: string;
  ok?: boolean;
  tenantName?: string | null;
  message?: string | null;
  checks?: MaesterValidationCheck[];
}

export interface MaesterClientState {
  clientId: number;
  clientName: string;
  tenantId?: string | null;
  tenantName?: string | null;
  connectionStatus: 'connected' | 'not_connected' | 'permission_error' | string;
  permissionStatus?: 'ok' | 'not_validated' | 'missing_or_invalid' | string;
  validationDetails?: MaesterValidationDetails;
  authMethod?: 'none' | 'certificate' | 'secret' | string;
  appClientId?: string | null;
  certificateThumbprint?: string | null;
  certificateExpiresAt?: string | null;
  hasCertificateCredential?: boolean;
  credentialStorageProvider?: string | null;
  githubEnvironmentName?: string | null;
  githubSecretNames?: string[];
  githubSecretsConfiguredAt?: string | null;
  frequency: MaesterFrequency;
  nextScanAt?: string | null;
  consentedAt?: string | null;
  lastValidationAt?: string | null;
  lastScanAt?: string | null;
  lastError?: string | null;
  latestScan?: MaesterLatestScan | null;
}

export interface MaesterOverview {
  configured: boolean;
  graphValidation?: {
    hasClientId: boolean;
    hasClientSecret: boolean;
  };
  githubSecrets?: {
    owner: string;
    repo: string;
    hasToken: boolean;
  };
  summary: {
    clientCount: number;
    connectedCount: number;
    scheduledCount: number;
    lastScanCount: number;
  };
  clients: MaesterClientState[];
}

export interface MaesterConnectUrlResponse {
  clientId: number;
  clientName: string;
  configured: boolean;
  reason?: string;
  consentUrl?: string | null;
  redirectUri?: string;
}

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || response.statusText || 'Erreur API Maester');
  }
  return data as T;
}

export async function getMaesterOverview(): Promise<MaesterOverview> {
  const response = await fetch('/api/prisme/cyber/maester/clients', { credentials: 'include' });
  return readJson<MaesterOverview>(response);
}

export async function getMaesterConnectUrl(clientId: number): Promise<MaesterConnectUrlResponse> {
  const response = await fetch(`/api/prisme/cyber/maester/clients/${clientId}/connect-url`, { credentials: 'include' });
  return readJson<MaesterConnectUrlResponse>(response);
}

export async function saveMaesterFrequency(clientId: number, frequency: MaesterFrequency): Promise<void> {
  const response = await fetch(`/api/prisme/cyber/maester/clients/${clientId}/settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ frequency }),
  });
  await readJson(response);
}

export async function saveMaesterConnection(clientId: number, tenantId: string, tenantName?: string): Promise<void> {
  const response = await fetch(`/api/prisme/cyber/maester/clients/${clientId}/connection`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ tenantId, tenantName }),
  });
  await readJson(response);
}

export interface MaesterCertificateCredentialInput {
  tenantId: string;
  tenantName?: string;
  appClientId: string;
  certificatePem: string;
  privateKeyPem: string;
  certificateExpiresAt?: string;
}

export interface MaesterGithubCredentialDepositInput {
  tenantId: string;
  tenantName?: string;
  appClientId: string;
  githubEnvironmentName?: string;
  authMethod: 'github_secret' | 'github_certificate';
  clientSecret?: string;
  certificatePfxBase64?: string;
  certificatePassword?: string;
  certificateExpiresAt?: string;
}

export async function saveMaesterCertificateCredential(
  clientId: number,
  input: MaesterCertificateCredentialInput
): Promise<{ validation: MaesterValidationDetails }> {
  const response = await fetch(`/api/prisme/cyber/maester/clients/${clientId}/certificate-credential`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson(response);
}

export async function depositMaesterGithubCredentials(
  clientId: number,
  input: MaesterGithubCredentialDepositInput
): Promise<{ validation?: MaesterValidationDetails; message?: string; environmentName: string; secretNames: string[] }> {
  const response = await fetch(`/api/prisme/cyber/maester/clients/${clientId}/github-secrets`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson(response);
}

export async function validateMaesterConnection(clientId: number, tenantId: string): Promise<{ validation: MaesterValidationDetails }> {
  const response = await fetch(`/api/prisme/cyber/maester/clients/${clientId}/validate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ tenantId }),
  });
  return readJson(response);
}

export async function queueMaesterFullScan(clientId: number, tenantId?: string | null): Promise<{ queued: boolean; message?: string }> {
  const response = await fetch(`/api/prisme/cyber/maester/clients/${clientId}/scans`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ tenantId }),
  });
  return readJson(response);
}
